import { ApiClient } from '@api/api-configuration';
import { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import AppAvatar from '@app/components/ui/Avatar';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { SelectProps } from 'antd';
import { memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The fields this picker actually reads off a caregiver.
 *
 * `getItemId` reads the id and `getItemLabel`/`renderOption` read the two names — that is the
 * whole of it. Declared structurally so a caller holding less than a full `CaregiverMinified`
 * (an availability answer, say) can restrict this picker without inventing the rest of the row.
 */
export interface CaregiverChoice {
  caregiverId: number;
  firstName: string;
  lastName: string;
}

type CaregiversSelectProps = {
  onCaregiverChange?: (caregiver: CaregiverMinified | CaregiverMinified[] | undefined) => void;
  placeholder?: string;
  pageSize?: number;
  minSearchLength?: number;
  searchDebounceMs?: number;
  filterServiceIds?: number[];
  reloadSignal?: number | string;
  lazyLoadOnOpen?: boolean;
  selectedItemsProvider?: () => CaregiverMinified | CaregiverMinified[] | undefined;
  active?: boolean | null;
  /**
   * Restrict the picker to these caregivers, and these only.
   *
   * WHY THIS IS NOT A QUERY PARAMETER
   * ────────────────────────────────
   * `GET /caregivers` filters by `query`, `services`, `active`, `city` and `skills` — there is no
   * id filter — so "offer exactly these people" cannot be asked of the server. Filtering the
   * server's answer client-side does not work either: this picker PAGES, so page 1 of the roster
   * routinely contains none of the wanted people and the dropdown would look empty while
   * somebody eligible sat on page 3.
   *
   * So when this is supplied the picker stops fetching and serves from it instead — searched and
   * paged locally, through the same {@link ApiFetchFunction} contract, so every other behaviour
   * (the search box, infinite scroll, the option rendering, the selected-item union) is
   * unchanged. The caller is expected to hold a COMPLETE set, which is what makes local paging
   * honest rather than a truncation.
   *
   * The arrangement screen's caller is line availability — the caregivers free on every day a
   * line covers — which carries names and not just a count, hence complete.
   *
   * `undefined`/`null` means no restriction: fetch the roster, exactly as before. An EMPTY ARRAY
   * is a restriction to nobody and renders an empty dropdown, which is a real answer and must
   * not be confused with the absence of one.
   */
  restrictTo?: readonly CaregiverChoice[] | null;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch'>;

/** Matches the free-text box against a caregiver's name, the way a reader would expect. */
const matchesQuery = (caregiver: CaregiverChoice, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return `${caregiver.firstName} ${caregiver.lastName}`.toLowerCase().includes(needle);
};

// Memoized option component for performance
const CaregiverOption = memo(({ firstName, lastName }: { firstName: string; lastName: string }) => {
  const fullname = `${firstName} ${lastName}`;
  const fullnameLetters = getInitials(fullname, { fallback: '' });

  return (
    <div className="flex items-center gap-2 min-w-0 w-full">
      <span className="flex-none">
        <AppAvatar isSelected={true} fullName={fullname} value={fullnameLetters} />
      </span>
      <span className="flex-1 min-w-0 truncate">{`${firstName}, ${lastName}`}</span>
    </div>
  );
});
CaregiverOption.displayName = 'CaregiverOption';

export const CaregiversSelectGeneric = ({
  onCaregiverChange,
  placeholder,
  pageSize = 10,
  minSearchLength = 3,
  searchDebounceMs = 300,
  filterServiceIds,
  reloadSignal,
  lazyLoadOnOpen,
  selectedItemsProvider,
  active = true,
  restrictTo,
  ...selectProps
}: CaregiversSelectProps) => {
  const { t } = useTranslation();

  /**
   * The restriction's identity — what has to change before the picker reloads.
   *
   * A caller derives `restrictTo` from an availability answer, so it is a FRESH ARRAY on every
   * render even when the people in it are the same. Keying on the ids instead is what keeps that
   * from rebuilding the fetcher (and so refetching) on every keystroke elsewhere on the screen.
   *
   * `'api'` is the unrestricted case, and it is a distinct value from `'r:'` — a restriction to
   * NOBODY, which is an answer and must not collapse into "no restriction".
   */
  const restrictionKey = restrictTo ? `r:${restrictTo.map((caregiver) => caregiver.caregiverId).join(',')}` : 'api';

  /**
   * What an empty dropdown SAYS when the restriction is the reason it is empty.
   *
   * `restrictTo: []` is an answer — "asked, and nobody is free for these visits" — and it
   * restricts this picker to nobody, deliberately. Without this, antd fills that dropdown with
   * its default "No Data", which reads as a screen that failed to load a roster of 41 people;
   * it was reported as exactly that ("the caregivers dropdown is always empty, not sure why").
   * `AvailableCaregivers.tsx`'s module note already said the dropdown's own `notFoundContent`
   * was what explained the narrowing — no caller ever passed one, so nothing did.
   *
   * Only for the RESTRICTED-to-nobody case. When the picker is unrestricted, an empty result is a
   * search that matched nobody, and claiming "none are available for these visits" would be a
   * different and untrue statement. A caller's own `notFoundContent` still wins.
   */
  const restrictedToNobody = restrictTo !== null && restrictTo !== undefined && restrictTo.length === 0;

  // In a ref so the memo below can read the current set without listing the array itself as a
  // dependency, which would defeat the keying above.
  const restrictToRef = useRef(restrictTo);
  restrictToRef.current = restrictTo;

  const serviceKey = filterServiceIds?.join(',') ?? '';

  /**
   * One fetcher, either source, served through the SAME contract.
   *
   * Satisfying `ApiFetchFunction` for the restricted case rather than adding a second code path
   * is the point: the search box, the infinite scroll, the option rendering and the selected-item
   * union all keep working because none of them can tell where a page came from.
   */
  const fetchCaregivers = useMemo<ApiFetchFunction<CaregiverMinified>>(
    () => async (params: ApiQueryParams) => {
      const allowed = restrictToRef.current;

      if (allowed) {
        const matched = allowed.filter((caregiver) => matchesQuery(caregiver, params.query ?? ''));
        return {
          data: {
            // The three fields this picker reads ARE on `CaregiverChoice`; the cast says the rest
            // of the row is knowingly absent rather than accidentally lost. The same trade the
            // arrangement draft makes to label a caregiver it has not fetched a page containing.
            data: matched.slice(params.offset, params.offset + params.limit) as unknown as CaregiverMinified[],
            // AFTER the search filter, which is what the component pages against — the unfiltered
            // length would make it ask for a page that does not exist.
            total: matched.length,
            offset: params.offset,
          },
        };
      }

      return ApiClient.caregiverApi.caregivers.listCaregivers({
        ...params,
        ...(serviceKey.length > 0 ? { services: filterServiceIds } : {}),
        active,
      });
    },
    // `filterServiceIds` is read through `serviceKey`, which is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restrictionKey, serviceKey, active]
  );

  return (
    <GenericSearchSelect<CaregiverMinified>
      {...(restrictedToNobody && { notFoundContent: t('service-requests-caregiver-none-available') })}
      {...selectProps}
      apiFetch={fetchCaregivers}
      getItemId={(caregiver) => caregiver.caregiverId}
      getItemLabel={(caregiver) => `${caregiver.firstName} ${caregiver.lastName}`}
      onSelectionChange={onCaregiverChange}
      placeholder={placeholder ?? t('caregivers-select-placeholder')}
      pageSize={pageSize}
      minSearchLength={minSearchLength}
      searchDebounceMs={searchDebounceMs}
      searchPlaceholder={t('caregivers-search-select-placeholder')}
      // The restriction is part of what the loaded page depends on, so a change to it has to
      // reload — otherwise a picker that opened before the availability answer landed would go on
      // offering the roster it had already fetched.
      reloadSignal={`${reloadSignal ?? ''}|${restrictionKey}`}
      lazyLoadOnOpen={lazyLoadOnOpen}
      selectedItemsProvider={selectedItemsProvider}
      renderOption={(caregiver) => <CaregiverOption firstName={caregiver.firstName} lastName={caregiver.lastName} />}
    />
  );
};

export default CaregiversSelectGeneric;
